import type { MouseEvent } from 'react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLenis } from '../scroll/scroll';
import { useI18n } from '../../i18n';
import './content-index.scss';

interface ContentIndexProps {
  rootSelector?: string;
  headingLevels?: Array<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>;
}

interface HeadingItem {
  id: string;
  text: string;
  level: string;
}

const defaultHeadingLevels: Array<'h1' | 'h2'> = ['h1', 'h2'];

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-]/g, '');

export function ContentIndex({ rootSelector = 'main.page', headingLevels = defaultHeadingLevels }: ContentIndexProps) {
  const { language } = useI18n();
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [toastVisible, setToastVisible] = useState(false);
  const lenis = useLenis();
  const navRef = useRef<HTMLElement>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Ref so DOM event listeners always call the latest version without re-injection
  const onCopyRef = useRef<(headingId: string) => void>();

  const showToast = useCallback(() => {
    setToastVisible(true);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 3000);
  }, []);

  // Keep ref current on every render (no deps needed)
  onCopyRef.current = (headingId: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${headingId}`;
    navigator.clipboard.writeText(url).then(() => showToast());
    window.history.pushState(null, '', `#${headingId}`);
  };

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const root = el.closest(rootSelector) || document.querySelector(rootSelector);
    if (!root) return;

    const levels = headingLevels ?? defaultHeadingLevels;
    const headingNodes = Array.from(root.querySelectorAll<HTMLElement>(levels.join(',')));

    const items = headingNodes.map((heading, index) => {
      const text = heading.textContent?.trim() || `heading-${index + 1}`;
      const baseId = heading.id || slugify(text) || `heading-${index + 1}`;
      const id = heading.id || `${baseId}-${index + 1}`;

      if (!heading.id) {
        heading.id = id;
      }

      // Inject floating anchor button if not already present
      if (!heading.querySelector('.heading-anchor')) {
        const anchor = document.createElement('button');
        anchor.className = 'heading-anchor';
        anchor.textContent = '#';
        anchor.setAttribute('aria-label', 'Copy link to section');
        anchor.setAttribute('type', 'button');
        const capturedId = id;
        anchor.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          onCopyRef.current?.(capturedId);
        });
        heading.appendChild(anchor);
      }

      return { id, text, level: heading.tagName.toLowerCase() };
    });

    setHeadings(items);

    // Scroll to hash on initial load
    const hash = window.location.hash.slice(1);
    if (hash) {
      const target = root.querySelector<HTMLElement>(`#${hash}`);
      if (target) {
        requestAnimationFrame(() => {
          if (lenis) {
            lenis.scrollTo(target, { offset: -200, duration: 1.2 });
          } else {
            target.scrollIntoView({ behavior: 'smooth' });
            window.scrollBy(0, -200);
          }
        });
      }
    }

    return () => {
      headingNodes.forEach((heading) => heading.querySelector('.heading-anchor')?.remove());
    };
  }, [rootSelector, headingLevels, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, headingId: string) => {
    event.preventDefault();
    const el = navRef.current;
    if (!el) return;

    const root = el.closest(rootSelector) || document.querySelector(rootSelector);
    if (!root) return;

    const target = root.querySelector(`#${headingId}`);
    if (!target) return;

    window.history.pushState(null, '', `#${headingId}`);

    if (lenis) {
      lenis.scrollTo(target as HTMLElement, {
        offset: -200,
        duration: 1.2,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
      return;
    }

    target.scrollIntoView({ behavior: 'smooth' });
    window.scrollBy(0, -200);
  };

  return (
    <>
      <nav
        ref={navRef}
        className="content-index"
        aria-label="Content index"
        style={{ display: headings.length <= 1 ? 'none' : undefined }}
      >
        {headings.map((heading) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            className={`content-index__item content-index__item--${heading.level}`}
            aria-label={heading.text}
            onClick={(event) => handleClick(event, heading.id)}
          >
            <span className="content-index__marker" />
            <span className="content-index__label">{heading.text}</span>
          </a>
        ))}
      </nav>

      {createPortal(
        <div
          className="heading-anchor-toast"
          aria-live="polite"
          aria-atomic="true"
          data-visible={toastVisible ? 'true' : 'false'}
        >
          <span>Link to content copied</span>
        </div>,
        document.body
      )}
    </>
  );
}
